import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, timeAgo } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/shared/Common";
import { DateRangePicker } from "@/components/shared/DateRangePicker";
import { Pagination } from "@/components/shared/Pagination";
import { Button } from "@/components/ui/button";
import { Bell } from "@phosphor-icons/react";

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (dateRange?.from) q.set("date_from", dateRange.from);
    if (dateRange?.to) q.set("date_to", dateRange.to);
    q.set("limit", String(pageSize));
    q.set("skip", String((page - 1) * pageSize));
    const { data } = await api.get(`/notifications?${q.toString()}`);
    setItems(data.items);
    setTotal(data.total || data.items.length);
  }, [dateRange, page, pageSize]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dateRange, pageSize]);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    if (n.link) nav(n.link);
    load();
  };

  return (
    <div>
      <PageHeader title="Notifications" subtitle={`${items.filter(i => !i.read).length} unread on this page`}>
        <DateRangePicker testId="notifications-date-range" value={dateRange} onChange={setDateRange} />
        <Button data-testid="mark-all-read-btn" variant="outline" className="h-9" onClick={markAll}>Mark all as read</Button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState title="No notifications" subtitle="You're all caught up." />
      ) : (
        <div className="border rounded-md bg-card">
          <div className="divide-y">
            {items.map(n => (
              <div key={n.id} onClick={() => open(n)} className={`p-4 flex items-start gap-3 cursor-pointer hover:bg-secondary/40 ${!n.read ? "bg-primary/5" : ""}`}>
                <div className={`w-8 h-8 rounded grid place-items-center shrink-0 ${!n.read ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
                  <Bell size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{n.title}</div>
                  <div className="text-xs text-muted-foreground">{n.body}</div>
                  <div className="text-[10px] mono text-muted-foreground mt-1">{timeAgo(n.at)}</div>
                </div>
                {!n.read && <div className="w-2 h-2 bg-primary rounded-full mt-2" />}
              </div>
            ))}
          </div>
          <Pagination testId="notifications-pagination" page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={setPageSize} />
        </div>
      )}
    </div>
  );
}
